import { useId, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useUploadDocument } from '@/features/patient-record/hooks/useDocumentMutations';
import type { DocumentType } from '@/features/patient-record/types';

const DOCUMENT_TYPES: readonly DocumentType[] = [
  'DOCUMENT',
  'MEDICAL_REPORT',
  'IMAGE',
  'QUESTIONNAIRE',
];

interface DocumentComposerProps {
  readonly guid: string;
  readonly onCancel: () => void;
  readonly onSaved: () => void;
}

interface DocumentDraft {
  readonly title: string;
  readonly type: DocumentType;
  readonly file: File | null;
}

const toDraft = (): DocumentDraft => ({
  title: '',
  type: 'MEDICAL_REPORT',
  file: null,
});

export function DocumentComposer({
  guid,
  onCancel,
  onSaved,
}: DocumentComposerProps): ReactElement {
  const { t } = useTranslation();
  const mutation = useUploadDocument(guid);
  const reactId = useId();
  const typeId = `${reactId}-type`;
  const titleId = `${reactId}-title`;
  const fileId = `${reactId}-file`;

  const toInput = (d: DocumentDraft) => ({
    title: d.title.trim(),
    documentType: d.type,
    ...(d.file !== null ? { file: d.file } : {}),
  });

  // PTV2-1327: the primitive owns the draft. This is an add-only composer, so autosave is OFF —
  // a document is uploaded only via the explicit Upload button. Registered while dirty so leaving /
  // switching view mode prompts instead of dropping the input. A File isn't structurally comparable,
  // so `isEqual` folds it in by identity alongside title/type.
  const form = useEditableDraft<DocumentDraft>({
    initial: toDraft(),
    label: t('patient.documents.formLabel', { defaultValue: 'Document' }),
    autosave: false,
    canSave: (d) => d.title.trim().length > 0,
    isEqual: (a, b) => a.title === b.title && a.type === b.type && a.file === b.file,
    onSave: (d) => mutation.mutateAsync(toInput(d)),
  });
  const { draft } = form;

  const canSubmit = draft.title.trim().length > 0 && !mutation.isPending;

  // The explicit Upload button: commit AND close.
  const submitAndClose = (): void => {
    if (draft.title.trim().length === 0) return;
    void mutation.mutateAsync(toInput(draft)).then(onSaved).catch(() => undefined);
  };

  return (
    <form
      ref={form.containerProps.ref}
      className="rounded-md border border-border bg-muted/30 p-3 space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        submitAndClose();
      }}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex flex-col gap-1 sm:w-40">
          <label htmlFor={typeId} className="text-xs font-medium text-card-foreground">
            {t('patient.documents.typeLabel')}
          </label>
          <Select
            id={typeId}
            value={draft.type}
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, type: event.target.value as DocumentType }));
            }}
            className="h-9 text-sm"
          >
            {DOCUMENT_TYPES.map((doctype) => (
              <option key={doctype} value={doctype}>
                {t(`patient.documents.type.${doctype}`)}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <label htmlFor={titleId} className="text-xs font-medium text-card-foreground">
            {t('patient.documents.titleLabel')}
            <span className="ml-0.5 text-destructive" aria-hidden="true">*</span>
          </label>
          <Input
            id={titleId}
            type="text"
            value={draft.title}
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, title: event.target.value }));
            }}
            placeholder={t('patient.documents.titlePlaceholder')}
            className="h-9 text-sm"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={fileId} className="text-xs font-medium text-card-foreground">
          {t('patient.documents.fileLabel')}
        </label>
        <input
          id={fileId}
          type="file"
          onChange={(event) => {
            const next = event.target.files?.[0] ?? null;
            form.setDraft((d) => ({ ...d, file: next }));
          }}
          className="text-xs file:mr-3 file:rounded file:border file:border-input file:bg-background file:px-2 file:py-1 file:text-xs"
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onCancel}
          disabled={mutation.isPending}
        >
          {t('ui.dialog.close')}
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!canSubmit}
          loading={mutation.isPending}
          loadingText={t('patient.dirtyFooter.saving')}
        >
          {t('patient.documents.upload')}
        </Button>
      </div>
    </form>
  );
}
